import type { HandResult, Rank, Seat, Team } from "@guandan/engine";
import type { TableAction } from "@guandan/protocol";

export interface CompletedHandView {
  readonly handNumber: number;
  readonly level: Rank;
  readonly winningTeam: Team;
  readonly order: readonly Seat[];
}

export interface TableTrickGroup {
  readonly trick: number;
  readonly actions: readonly TableAction[];
}

/** Group the authoritative table log by trick without inferring pass runs. */
export function groupTableActionsByTrick(actions: readonly TableAction[]): TableTrickGroup[] {
  const groups = new Map<number, TableAction[]>();
  const seen = new Set<number>();
  for (const action of [...actions].sort((a, b) => a.seq - b.seq)) {
    if (seen.has(action.seq)) continue;
    seen.add(action.seq);
    const group = groups.get(action.trick);
    if (group === undefined) groups.set(action.trick, [action]);
    else group.push(action);
  }
  return [...groups].map(([trick, groupedActions]) => ({ trick, actions: groupedActions }));
}

export function completedHandOrder(result: Pick<HandResult, "finished" | "unfinished">): Seat[] {
  return [...result.finished, ...result.unfinished].slice(0, 4);
}

export function rememberCompletedHand(
  history: readonly CompletedHandView[],
  handNumber: number,
  level: Rank,
  result: Pick<HandResult, "finished" | "unfinished" | "winningTeam">,
): CompletedHandView[] {
  if (history.some((hand) => hand.handNumber === handNumber)) return [...history];
  return [...history, {
    handNumber,
    level,
    winningTeam: result.winningTeam,
    order: completedHandOrder(result),
  }];
}
